<template>
  <transition name="release-update-notice">
    <aside
      v-if="notice.visible"
      class="release-update-notice"
      :class="{ 'release-update-notice--blocking': notice.blocking }"
      :aria-live="notice.blocking ? 'assertive' : 'polite'"
      role="status"
    >
      <button
        v-if="notice.dismissable"
        type="button"
        class="release-update-notice__dismiss"
        :aria-label="$gettext('Dismiss update notice')"
        @click="dismissNotice"
      >
        <font-awesome-icon icon="fa-solid fa-xmark" />
      </button>

      <div class="release-update-notice__eyebrow">{{ notice.eyebrow }}</div>
      <h3 class="release-update-notice__title">{{ notice.title }}</h3>
      <p v-if="notice.message" class="release-update-notice__message">{{ notice.message }}</p>

      <div class="release-update-notice__actions">
        <GekkoButton :text="$gettext('Refresh now')" icon="fa-solid fa-rotate-right" @click="refreshNow" />
      </div>
    </aside>
  </transition>
</template>

<script>
/* global __GEKKO_RELEASE_VERSION__ */

import GekkoButton from '@/components/GekkoButton'

const loadedReleaseVersion = typeof __GEKKO_RELEASE_VERSION__ !== 'undefined' ? __GEKKO_RELEASE_VERSION__ : null

export default {
  name: 'ReleaseUpdateNotice',
  components: {
    GekkoButton,
  },
  props: {
    pollInterval: {
      type: Number,
      default: 60000,
    },
  },
  data() {
    return {
      loadedReleaseVersion,
      latestReleaseVersion: null,
      releaseVersionBlockerAlertShown: false,
      releaseVersionToastAlertShown: false,
      releaseVersionPollIntervalId: null,
      notice: {
        visible: false,
        blocking: false,
        dismissable: true,
        eyebrow: '',
        title: '',
        message: '',
      },
    }
  },
  mounted() {
    const url = new URL(window.location.href)
    if (url.searchParams.has('_cb')) {
      url.searchParams.delete('_cb')
      window.history.replaceState(null, '', url.toString())
      // After a forced reload, accept the version we expected so the notice doesn't
      // immediately re-fire if the old bundle was still served (e.g. stale webpack_loader
      // cache in production). Future deployments will still be detected correctly.
      const expectedVersion = sessionStorage.getItem('_gekko_expected_version')
      if (expectedVersion) {
        this.loadedReleaseVersion = expectedVersion
      }
    }
    sessionStorage.removeItem('_gekko_expected_version')
    this.startReleaseVersionPolling()
  },
  destroyed() {
    if (this.releaseVersionPollIntervalId) {
      clearInterval(this.releaseVersionPollIntervalId)
    }
  },
  methods: {
    startReleaseVersionPolling() {
      this.loadReleaseVersion()

      this.releaseVersionPollIntervalId = window.setInterval(() => {
        this.loadReleaseVersion()
      }, this.pollInterval)
    },
    async loadReleaseVersion() {
      try {
        const response = await fetch(`${process.env.BASE_URL}release-version.json`, {
          cache: 'no-store',
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }

        const payload = await response.json()
        this.latestReleaseVersion = payload.release_uuid || null
        const updateLevel = payload.update_level || 'silent'
        const isCurrent = this.loadedReleaseVersion === this.latestReleaseVersion

        if (isCurrent) {
          this.releaseVersionBlockerAlertShown = false
          this.releaseVersionToastAlertShown = false
          this.hideNotice()
        } else if (updateLevel === 'blocker' && !this.releaseVersionBlockerAlertShown) {
          this.releaseVersionBlockerAlertShown = true
          this.showNotice({
            blocking: true,
            dismissable: false,
            eyebrow: this.$gettext('Action needed'),
            title: this.$gettext('Gekko needs to reload'),
            message: this.$gettext(
              "We've made changes that aren't compatible with this session. Refresh to keep things running smoothly."
            ),
          })
        } else if (updateLevel === 'toast' && !this.releaseVersionToastAlertShown) {
          this.releaseVersionToastAlertShown = true
          this.showNotice({
            blocking: false,
            dismissable: true,
            eyebrow: this.$gettext("We've been busy"),
            title: this.$gettext('Gekko was updated in the background'),
            message: this.$gettext("Refresh whenever you're ready to get the latest."),
          })
        }

        console.log('[release-version]', {
          loaded: this.loadedReleaseVersion,
          latest: this.latestReleaseVersion,
          updateLevel,
          isCurrent,
        })
      } catch (error) {
        console.warn('[release-version] failed to load', error)
      }
    },
    showNotice({ blocking, dismissable, eyebrow, title, message }) {
      this.notice = {
        visible: true,
        blocking,
        dismissable,
        eyebrow,
        title,
        message,
      }
    },
    hideNotice() {
      this.notice = {
        visible: false,
        blocking: false,
        dismissable: true,
        eyebrow: '',
        title: '',
        message: '',
      }
    },
    dismissNotice() {
      if (!this.notice.dismissable) {
        return
      }

      this.hideNotice()
    },
    refreshNow() {
      console.log('refreshNow')
      sessionStorage.setItem('_gekko_expected_version', this.latestReleaseVersion)
      const url = new URL(window.location.href)
      url.searchParams.set('_cb', Date.now())
      window.location.replace(url.toString())
    },
  },
}
</script>

<style lang="scss" scoped>
.release-update-notice {
  position: fixed;
  left: 1rem;
  bottom: 1rem;
  z-index: 1400;
  width: min(28rem, calc(100vw - 2rem));
  padding: 1rem;
  border: 1px solid $colorBrand200;
  border-radius: 16px;
  background: linear-gradient(180deg, $colorBrand10 0%, #ffffff 100%);
  box-shadow: 0 20px 45px rgba(15, 32, 28, 0.18);
  color: $colorTextBase;
}

.release-update-notice--blocking {
  border-color: $colorOrange200;
  background: linear-gradient(180deg, $colorOrange50 0%, #ffffff 100%);
}

.release-update-notice__dismiss {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: $colorTextSemi;
}

.release-update-notice__dismiss:hover {
  background: rgba(15, 32, 28, 0.06);
}

.release-update-notice__eyebrow {
  margin-bottom: 0.35rem;
  padding-right: 2rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $colorBrand;
}

.release-update-notice--blocking .release-update-notice__eyebrow {
  color: $colorOrange;
}

.release-update-notice__title {
  margin: 0;
  padding-right: 2rem;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.2;
}

.release-update-notice__message,
.release-update-notice__content {
  margin-top: 0.65rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #42514c;
}

.release-update-notice__message {
  margin-bottom: 0;
}

.release-update-notice__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
}

.release-update-notice-enter-active,
.release-update-notice-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.release-update-notice-enter,
.release-update-notice-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

@media (max-width: 767px) {
  .release-update-notice {
    left: 0.75rem;
    right: 0.75rem;
    bottom: 0.75rem;
    width: auto;
  }
}
</style>